import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  KeyboardAvoidingView,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';
import type { RootStackParamList } from '../../types/navigation';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SignIn'>;
  route: RouteProp<RootStackParamList, 'SignIn'>;
};

export default function SignInScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signInWithGoogle, signInWithApple } = useAuth();

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      // OAuth will redirect, so no need to navigate here
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithApple();
      // OAuth will redirect, so no need to navigate here
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <KeyboardAvoidingView style={styles.flex} behavior="padding">
        <View style={[commonStyles.screenContainer, styles.formWrapper]}>
          <View style={commonStyles.form}>
            <Text style={commonStyles.screenTitle}>Welcome to Cache</Text>
            <Text style={commonStyles.screenSubtitle}>Sign in to continue</Text>

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={handleGoogleSignIn}
              disabled={loading}
            >
              <Ionicons name="logo-google" size={20} color="#DB4437" />
              <Text style={commonStyles.socialButtonText}>Continue with Google</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={handleAppleSignIn}
              disabled={loading}
            >
              <Ionicons name="logo-apple" size={20} color={theme.colors.textPrimary} />
              <Text style={commonStyles.socialButtonText}>Continue with Apple</Text>
            </TouchableOpacity>

            <View style={commonStyles.separator}>
              <View style={commonStyles.separatorLine} />
              <Text style={commonStyles.separatorText}>or</Text>
              <View style={commonStyles.separatorLine} />
            </View>

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={() => navigation.navigate('EmailSignIn', { redirect: route.params?.redirect })}
              disabled={loading}
            >
              <Text style={commonStyles.socialButtonText}>Continue with Email</Text>
            </TouchableOpacity>

            {error && <Text style={commonStyles.formErrorText}>{error}</Text>}

            <TouchableOpacity
              style={commonStyles.link}
              onPress={() => navigation.navigate('SignUp')}
              disabled={loading}
            >
              <Text style={commonStyles.linkText}>
                Don't have an account? Sign up
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
});
